/**
 * lib/label.ts — the two lines the labels «Ямайская» and «Студия» carry: the track's title and
 * performer, at the same places on both (`lib/labels.ts`, `WITH_LINES`).
 *
 * Task 24. `spec.md` «Каталог и данные»: the label is a ready picture into which the title and
 * the performer are written; nothing about it is generated. This module decides how each line
 * is written — its size and, past a limit, where it is cut — and `components/RecordLabel.tsx`
 * writes it into the picture. Pure: no page, no fonts.
 *
 * ## Why the width is estimated, not measured
 *
 * The label is a 200-unit drawing that turns; its lines are set once per track. Measuring text
 * needs a font and a canvas, which jsdom lacks and which differ from phone to phone; an estimate
 * from the letters themselves is the same everywhere, and a line it places is sure to fit — the
 * letter widths are taken on the wide side of a bold system sans (`letterEm`). Wide letters
 * (Ж, Ш, M, W) count wider than narrow ones (I, 1, the space): a Cyrillic line is not a Latin
 * line of the same length.
 *
 * ## Fitting a line
 *
 * A line is written at its place's largest size if it fits. Longer, the size goes down until
 * it does — to {@link LineBox.minSize} and no lower: smaller than that is not read on a turning
 * record. Longer still, the line is cut and ends in «…». An empty line is no line: a track with
 * no performer has only its title on the label (`spec.md`: «исполнитель, которого нет, остаётся
 * пустым»).
 */

import type { RecordSize } from './records';

/** A line's place on the label, in the label's 200 units. */
export interface LineBox {
  /** The widest the line may be. */
  readonly maxWidth: number;
  readonly maxSize: number;
  readonly minSize: number;
  /** Letter spacing, in em. */
  readonly spacing: number;
}

export interface FittedLine {
  readonly text: string;
  /** Font size, in the label's units. */
  readonly size: number;
}

/**
 * Where the lines go on each label. The long-player's label is whole: the lines stand level
 * under the spindle. The single's is a ring round the adapter: the lines run along its lower arcs,
 * the title on the outer one — so they are shorter.
 */
export const LABEL_LINES: Readonly<
  Record<RecordSize, { readonly title: LineBox; readonly performer: LineBox }>
> = Object.freeze({
  '33': Object.freeze({
    title: Object.freeze({ maxWidth: 138, maxSize: 19, minSize: 11, spacing: 0.02 }),
    performer: Object.freeze({ maxWidth: 118, maxSize: 11, minSize: 7, spacing: 0.1 }),
  }),
  '45': Object.freeze({
    title: Object.freeze({ maxWidth: 150, maxSize: 13, minSize: 8, spacing: 0.04 }),
    performer: Object.freeze({ maxWidth: 118, maxSize: 9, minSize: 6, spacing: 0.1 }),
  }),
});

/** Letters' widths in em, on the wide side of a bold system sans. */
const NARROW = new Set([..." .,:;!'|IJilj1ІЇ()-–—·"]);
const WIDE = new Set([...'MWЖШЩЮМФЫ@%&']);

function letterEm(letter: string): number {
  if (NARROW.has(letter)) return 0.34;
  if (WIDE.has(letter)) return 0.95;
  if (/\d/u.test(letter)) return 0.62;
  if (letter === '…') return 0.9;
  return letter === letter.toLocaleLowerCase('ru') && letter !== letter.toLocaleUpperCase('ru')
    ? 0.58
    : 0.72;
}

/** The estimated width of `text` at `size`, with `spacing` em between the letters. */
export function lineWidth(text: string, size: number, spacing: number): number {
  const letters = [...text];
  const em = letters.reduce((sum, letter) => sum + letterEm(letter), 0);
  return (em + spacing * Math.max(0, letters.length - 1)) * size;
}

/** `text` fitted into `box`, or `undefined` for an empty line. */
export function fitLine(text: string, box: LineBox): FittedLine | undefined {
  const line = text.trim();
  if (line === '') return undefined;
  const atOne = lineWidth(line, 1, box.spacing);
  const size = Math.min(box.maxSize, box.maxWidth / atOne);
  if (size >= box.minSize) return { text: line, size };
  // At the smallest size and still too long: as much of it as fits, and «…».
  const letters = [...line];
  let kept = letters.length;
  let cut = `${line}…`;
  while (kept > 1) {
    kept -= 1;
    cut = `${letters.slice(0, kept).join('').trimEnd()}…`;
    if (lineWidth(cut, box.minSize, box.spacing) <= box.maxWidth) break;
  }
  return { text: cut, size: box.minSize };
}

/** The label's two lines for a track, in capitals, as a record's label prints them. */
export function labelLines(
  title: string,
  performer: string,
  size: RecordSize,
): { readonly title: FittedLine | undefined; readonly performer: FittedLine | undefined } {
  const places = LABEL_LINES[size];
  return {
    title: fitLine(title.toLocaleUpperCase('ru'), places.title),
    performer: fitLine(performer.toLocaleUpperCase('ru'), places.performer),
  };
}
